import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BellAlertIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon,
  CalculatorIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';

const StockThresholdTab = () => {
  const { products, updateProduct, fetchAllData } = useAppData();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [thresholdConfig, setThresholdConfig] = useState({
    id: '',
    name: '',
    currentStock: 0,
    minStock: 0,
    maxStock: 0,
    optimalOrderQuantity: 0,
    leadTime: 3, // Temps de réapprovisionnement en jours
    safetyStock: 0,
    reorderPoint: 0,
    averageDailyConsumption: 0
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [recalculateAll, setRecalculateAll] = useState(false);

  // Fonction pour calculer les points de réapprovisionnement et quantités optimales
  const calculateOptimalQuantities = (product) => {
    // Si nous n'avons pas d'historique de consommation, utiliser une estimation
    const estimatedDailyConsumption = Math.max(1, Math.round(product.stock * 0.01));
    
    // Stock de sécurité = Consommation journalière * facteur de sécurité (ici 2 jours)
    const safetyStock = estimatedDailyConsumption * 2;
    
    // Point de réapprovisionnement = Consommation pendant le délai + stock de sécurité
    const leadTime = product.leadTime || 3; // Valeur par défaut si non définie
    const reorderPoint = (estimatedDailyConsumption * leadTime) + safetyStock;
    
    // Quantité économique optimale (simplifiée)
    const setupCost = 100; // Coût de passation de commande (fixe)
    const holdingCost = product.price * 0.25; // Coût de détention (25% du prix)
    
    // Formule de Wilson simplifiée
    const optimalOrderQuantity = Math.ceil(
      Math.sqrt((2 * setupCost * (estimatedDailyConsumption * 365)) / holdingCost)
    );
    
    return {
      ...product,
      averageDailyConsumption: estimatedDailyConsumption,
      safetyStock,
      reorderPoint,
      optimalOrderQuantity,
      minStock: Math.min(product.minStock || 0, safetyStock),
      maxStock: Math.max(product.maxStock || 0, optimalOrderQuantity * 2)
    };
  };

  // Fetch product data
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Initial data load
    const loadData = async () => {
      try {
        await fetchAllData();
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    loadData();
    
    // Refresh data every 5 minutes
    const refreshInterval = setInterval(() => {
      loadData();
    }, 300000);
    
    // Cleanup on unmount
    return () => clearInterval(refreshInterval);
  }, []);

  // Recalculer et sauvegarder les seuils pour tous les produits
  useEffect(() => {
    const recalculateThresholds = async () => {
      if (products.length > 0 && recalculateAll) {
        setLoading(true);
        let updatedCount = 0;
        let failedCount = 0;
        
        // Update each product with calculated thresholds
        for (const product of products) {
          try {
            const calculatedProduct = calculateOptimalQuantities(product);
            // Save the calculated values to the product
            const updatedProduct = {
              ...product,
              minStock: calculatedProduct.minStock,
              maxStock: calculatedProduct.maxStock,
              leadTime: calculatedProduct.leadTime || 3,
              safetyStock: calculatedProduct.safetyStock,
              reorderPoint: calculatedProduct.reorderPoint,
              optimalOrderQuantity: calculatedProduct.optimalOrderQuantity,
              averageDailyConsumption: calculatedProduct.averageDailyConsumption
            };
            
            // Save changes using the updateProduct function
            const result = await updateProduct(updatedProduct);
            if (result) {
              updatedCount++;
            } else {
              failedCount++;
            }
          } catch (error) {
            console.error(`Error updating product ${product.name}:`, error);
            failedCount++;
          }
        }
        
        // Finish processing
        setRecalculateAll(false);
        setLoading(false);
        
        // Show success or error message
        if (failedCount === 0) {
          toast.success(`Les seuils de ${updatedCount} produits ont été recalculés avec succès`);
        } else {
          toast.error(`${updatedCount} produits mis à jour, ${failedCount} échecs`);
        }
        
        // Refresh the data to show updated values
        fetchAllData();
      }
    };
    
    recalculateThresholds();
  }, [products, recalculateAll, updateProduct, fetchAllData]);

  // Ouvrir la configuration d'un produit
  const openProductConfig = (product) => {
    const calculatedProduct = calculateOptimalQuantities(product);
    setThresholdConfig({
      id: calculatedProduct.id,
      name: calculatedProduct.name,
      currentStock: calculatedProduct.stock || 0,
      minStock: calculatedProduct.minStock || calculatedProduct.safetyStock || 0,
      maxStock: calculatedProduct.maxStock || calculatedProduct.optimalOrderQuantity * 2 || 0,
      optimalOrderQuantity: calculatedProduct.optimalOrderQuantity || 0,
      leadTime: calculatedProduct.leadTime || 3,
      safetyStock: calculatedProduct.safetyStock || 0,
      reorderPoint: calculatedProduct.reorderPoint || 0,
      averageDailyConsumption: calculatedProduct.averageDailyConsumption || 0
    });
    setSelectedProduct(calculatedProduct);
    setShowConfigModal(true);
  };

  // Sauvegarder la configuration
  const saveThresholdConfig = async () => {
    if (selectedProduct) {
      try {
        // Update the product with new threshold values
        const updatedProduct = {
          ...selectedProduct,
          minStock: thresholdConfig.minStock,
          maxStock: thresholdConfig.maxStock,
          leadTime: thresholdConfig.leadTime,
          safetyStock: thresholdConfig.safetyStock,
          reorderPoint: thresholdConfig.reorderPoint,
          optimalOrderQuantity: thresholdConfig.optimalOrderQuantity,
          averageDailyConsumption: thresholdConfig.averageDailyConsumption
        };
        
        // Save changes using the updateProduct function from context
        const result = await updateProduct(updatedProduct);
        
        if (result) {
          toast.success(`Configuration de seuils pour ${thresholdConfig.name} enregistrée`);
        } else {
          toast.error(`Erreur lors de la sauvegarde des seuils pour ${thresholdConfig.name}`);
        }
        
        setShowConfigModal(false);
      } catch (error) {
        console.error('Error saving threshold config:', error);
        toast.error(`Erreur: ${error.message || 'Impossible de sauvegarder les seuils'}`);
      }
    }
  };

  // Calculer l'état des produits par rapport à leurs seuils
  const getProductStatus = (product) => {
    const stock = product.stock || 0;
    const minStock = product.minStock || 0;
    
    if (stock <= 0) {
      return { status: 'out_of_stock', label: 'Rupture', color: 'bg-red-500 text-white' };
    } else if (stock <= minStock / 2) {
      return { status: 'critical', label: 'Critique', color: 'bg-red-500/20 text-red-400' };
    } else if (stock <= minStock) {
      return { status: 'low', label: 'Faible', color: 'bg-amber-500/20 text-amber-400' };
    } else {
      return { status: 'normal', label: 'Normal', color: 'bg-green-500/20 text-green-400' };
    }
  };

  // Catégoriser les produits par leur état de stock
  const outOfStockProducts = products.filter(p => (p.stock || 0) <= 0);
  const criticalProducts = products.filter(p => (p.stock || 0) > 0 && (p.stock || 0) <= (p.minStock || 0) / 2);
  const lowStockProducts = products.filter(p => (p.stock || 0) > (p.minStock || 0) / 2 && (p.stock || 0) <= (p.minStock || 0));
  const normalStockProducts = products.filter(p => (p.stock || 0) > (p.minStock || 0));

  // Stats pour le dashboard
  const stats = [
    {
      name: 'Rupture de stock',
      value: outOfStockProducts.length,
      color: 'from-red-600 to-red-700',
      icon: ExclamationTriangleIcon,
    },
    {
      name: 'Stock critique',
      value: criticalProducts.length,
      color: 'from-amber-600 to-amber-700',
      icon: BellAlertIcon,
    },
    {
      name: 'Stock faible',
      value: lowStockProducts.length,
      color: 'from-blue-600 to-blue-700',
      icon: BellAlertIcon,
    },
    {
      name: 'Stock normal',
      value: normalStockProducts.length,
      color: 'from-green-600 to-green-700',
      icon: CheckCircleIcon,
    },
  ];

  // Show loading state when recalculating or loading initial data
  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <ArrowPathIcon className="h-10 w-10 text-white/30 animate-spin mb-4" />
          <p className="text-white font-medium">Chargement des données...</p>
          {recalculateAll && (
            <p className="text-white/60 mt-2">Recalcul des seuils en cours</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Titre et explication */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center">
              <BellAlertIcon className="h-6 w-6 mr-2" />
              Seuils & Alertes de Stock
            </h3>
            <p className="text-white/60 mt-1">
              Configurez des seuils de réapprovisionnement et recevez des alertes automatiques
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setRecalculateAll(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              <CalculatorIcon className="h-5 w-5" />
              <span>Recalculer tous les seuils</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <p className="text-lg font-semibold text-white">{stat.name}</p>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mt-4">{stat.value}</p>
            <p className="text-white/60 text-sm mt-1">produits</p>
          </motion.div>
        ))}
      </div>

      {/* Liste des alertes */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h4 className="text-lg font-medium text-white">Alertes de stock actives</h4>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">État</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock actuel</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock min.</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Point de commande</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Qté optimale</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {[...outOfStockProducts, ...criticalProducts, ...lowStockProducts].map((product, index) => {
                const status = getProductStatus(product);
                const calculatedProduct = calculateOptimalQuantities(product);
                
                return (
                  <motion.tr 
                    key={product.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-white">{product.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {product.stock || 0} {product.unit || 'unités'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {product.minStock || calculatedProduct.minStock || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {calculatedProduct.reorderPoint}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {calculatedProduct.optimalOrderQuantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => openProductConfig(product)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <AdjustmentsHorizontalIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
              
              {[...outOfStockProducts, ...criticalProducts, ...lowStockProducts].length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-white/60">
                    Aucune alerte de stock active
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tous les produits */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h4 className="text-lg font-medium text-white">Configuration des seuils par produit</h4>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">État</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock actuel</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock min.</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock max.</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {products.map((product, index) => {
                const status = getProductStatus(product);
                
                return (
                  <motion.tr 
                    key={product.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-white">{product.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {product.stock || 0} {product.unit || 'unités'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {product.minStock || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                      {product.maxStock || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => openProductConfig(product)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de configuration */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-3xl border border-white/20 w-full max-w-2xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Configuration des seuils de stock</h3>
              <button 
                onClick={() => setShowConfigModal(false)}
                className="text-white/60 hover:text-white"
              >
                &times;
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Produit</label>
                <input 
                  type="text"
                  value={thresholdConfig.name}
                  readOnly
                  className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Stock actuel</label>
                  <input 
                    type="number"
                    value={thresholdConfig.currentStock}
                    readOnly
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Consommation journalière</label>
                  <input 
                    type="number"
                    value={thresholdConfig.averageDailyConsumption}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, averageDailyConsumption: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Stock minimum</label>
                  <input 
                    type="number"
                    value={thresholdConfig.minStock}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, minStock: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Stock maximum</label>
                  <input 
                    type="number"
                    value={thresholdConfig.maxStock}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, maxStock: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Délai de réapprovisionnement (jours)</label>
                  <input 
                    type="number"
                    value={thresholdConfig.leadTime}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, leadTime: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Stock de sécurité</label>
                  <input 
                    type="number"
                    value={thresholdConfig.safetyStock}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, safetyStock: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Point de réapprovisionnement</label>
                  <input 
                    type="number"
                    value={thresholdConfig.reorderPoint}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, reorderPoint: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Quantité optimale de commande</label>
                  <input 
                    type="number"
                    value={thresholdConfig.optimalOrderQuantity}
                    onChange={(e) => setThresholdConfig({...thresholdConfig, optimalOrderQuantity: Number(e.target.value)})}
                    className="bg-white/10 border border-white/20 rounded-xl w-full px-4 py-2.5 text-white"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={saveThresholdConfig}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StockThresholdTab; 